// Lo que dice Cumbre en el onboarding de marca (ADR-2026-10-01-onboarding-v2-cumbre-guia).
// Todo puro: los tramos del globo, el ritmo con que se escriben y cómo reacciona a lo que
// pega la persona. El hook que lo anima vive en `GuiaCumbre`.

import type { Plataforma } from './link';

export type EstiloDeTramo = 'saludo' | 'pregunta' | 'detalle';

export interface Tramo {
  texto: string;
  estilo: EstiloDeTramo;
}

/** Ms por letra. Rápido a propósito: la persona viene a hacer algo, no a leer. */
export const MS_POR_LETRA = 24;

/** La pausa que queda DESPUÉS de una letra: la puntuación respira. */
export function demoraDespuesDe(letra: string): number {
  if (letra === '.' || letra === '?' || letra === '!') return 300;
  if (letra === ',' || letra === ':') return 150;
  return MS_POR_LETRA;
}

/** Entre un tramo y el siguiente (del saludo a la pregunta, de la pregunta al detalle). */
export const PAUSA_ENTRE_TRAMOS = 380;

export function letrasTotales(tramos: Tramo[]): number {
  return tramos.reduce((n, t) => n + t.texto.length, 0);
}

/**
 * Cuántas letras de cada tramo se ven cuando se escribieron `n` en total. El tramo que se
 * está escribiendo es el único con un parcial; los de antes van enteros y los de después,
 * en cero.
 */
export function reparto(tramos: Tramo[], n: number): number[] {
  let resto = Math.max(0, n);
  return tramos.map((t) => {
    const aca = Math.min(t.texto.length, resto);
    resto -= aca;
    return aca;
  });
}

/** Cuánto esperar antes de escribir la letra `n` (contando desde 0). */
export function demoraAntesDe(tramos: Tramo[], n: number): number {
  if (n <= 0) return 0;
  let inicio = 0;
  for (const t of tramos) {
    const fin = inicio + t.texto.length;
    // La letra n es la primera de este tramo: la anterior cerró el tramo de antes.
    if (n === inicio) return PAUSA_ENTRE_TRAMOS;
    if (n < fin) return demoraDespuesDe(t.texto[n - inicio - 1]);
    inicio = fin;
  }
  return 0;
}

/**
 * En qué letra (contando todos los tramos) termina `palabra` dentro del tramo `estilo`.
 * Es el momento en que se dispara lo que la palabra nombra (los stickers de "logo",
 * "colores"…). -1 si no está.
 */
export function finDePalabra(tramos: Tramo[], estilo: EstiloDeTramo, palabra: string): number {
  let inicio = 0;
  for (const t of tramos) {
    if (t.estilo === estilo) {
      const i = t.texto.toLowerCase().indexOf(palabra.toLowerCase());
      return i < 0 ? -1 : inicio + i + palabra.length;
    }
    inicio += t.texto.length;
  }
  return -1;
}

// ─── El paso del link ─────────────────────────────────────────────────────────────

export const DIALOGO_DEL_LINK: Tramo[] = [
  { texto: '¡Hola! Soy el asistente de Atlas, tu guía en la plataforma.', estilo: 'saludo' },
  { texto: '¿Empezamos por tu marca?', estilo: 'pregunta' },
  { texto: 'Pegá el link de tu tienda online así puedo entender lo que vendés.', estilo: 'detalle' },
];

/**
 * Lo que Cumbre va a leer: los stickers de debajo del globo. El globo no los nombra (se
 * repetía con los stickers); saltan en cascada cuando Cumbre dice que va a entender lo que vende.
 */
export const LO_QUE_LEE = [
  { id: 'logo', etiqueta: 'Tu logo' },
  { id: 'colores', etiqueta: 'Tus colores' },
  { id: 'letra', etiqueta: 'Tu letra' },
  { id: 'tono', etiqueta: 'Tu tono' },
] as const;

/** Lo que, escrito en el detalle, arranca la cascada de stickers. */
export const STICKERS_SALTAN_CON = 'entender';

export type QueLee = (typeof LO_QUE_LEE)[number]['id'];

export interface Reaccion {
  /** Cambia cuando cambia lo que hay que decir: el globo lo reescribe sólo entonces. */
  id: string;
  texto: string;
}

/**
 * Qué dice Cumbre sobre lo que la persona pegó. null: nada nuevo que decir, el globo
 * vuelve al detalle de siempre.
 */
export function reaccionAlLink(plataforma: Plataforma | null): Reaccion | null {
  switch (plataforma) {
    case 'tiendanube':
      return { id: 'tiendanube', texto: 'Una tienda de Tiendanube. Perfecto, la conozco bien.' };
    case 'shopify':
      return { id: 'shopify', texto: 'Una tienda de Shopify. Perfecto, la conozco bien.' };
    case 'web':
      return { id: 'web', texto: 'Con eso es suficiente. Empiezo en cuanto presiones “Analizar mi marca”.' };
    case 'instagram':
      return {
        id: 'instagram',
        texto: 'Todavía no puedo leer Instagram. Con el link de tu web funciona, o podemos cargar la marca a mano.',
      };
    default:
      return null;
  }
}

export function reaccionAlEnviar(dominio: string): Reaccion {
  return { id: `enviando:${dominio}`, texto: `Analizando ${dominio}… un momento.` };
}

export const REACCION_SIN_WEB: Reaccion = { id: 'sin-web', texto: 'Perfecto, la cargamos juntos.' };

/** Si algo sale mal, Cumbre dice qué fue en el globo (no "abajo te cuento"). */
export function reaccionAlError(mensaje: string, n: number): Reaccion {
  return { id: `error-${n}`, texto: mensaje };
}

// ─── El tablero ───────────────────────────────────────────────────────────────────
// Lo que habla del análisis va en neutro (ADR-2026-10-02-onboarding-v2-analisis-en-neutro).

export function dialogoDelTablero(p: {
  estado: 'contando' | 'analizando' | 'listo' | 'incompleto';
  manual: boolean;
  /** El nombre de la marca AL TERMINAR: si se edita después, el globo no se reescribe. */
  nombre: string;
}): Tramo[] {
  const marca = p.nombre.trim() || 'tu marca';
  switch (p.estado) {
    case 'contando':
      return [
        { texto: 'Sin web, no hay problema.', estilo: 'saludo' },
        { texto: 'Hablemos de tu marca', estilo: 'pregunta' },
        { texto: 'Con esto armo tu paleta, tu tipografía y tu forma de comunicar.', estilo: 'detalle' },
      ];
    case 'analizando':
      // A mano no se encuentra nada ni llega por partes: la rama manual devuelve todo junto.
      return p.manual
        ? [
            { texto: 'Ya empecé a armar tu marca.', estilo: 'saludo' },
            { texto: 'Estoy creando tu marca', estilo: 'pregunta' },
            { texto: 'Con lo que me contaste armo tu paleta, tu tipografía y tu forma de comunicar.', estilo: 'detalle' },
          ]
        : [
            { texto: 'Ya empecé a analizar tu web.', estilo: 'saludo' },
            { texto: 'Esto es lo que encontré hasta ahora', estilo: 'pregunta' },
            { texto: 'El resto aparece apenas esté listo.', estilo: 'detalle' },
          ];
    case 'listo':
      return [
        { texto: '¡Listo!', estilo: 'saludo' },
        { texto: `Así veo a ${marca}`, estilo: 'pregunta' },
        {
          texto: p.manual
            ? 'La armé con lo que me contaste. Cualquier dato se puede corregir.'
            : 'Esto es todo lo que encontré. Cualquier dato se puede corregir.',
          estilo: 'detalle',
        },
      ];
    case 'incompleto':
      return [
        { texto: 'Casi listo.', estilo: 'saludo' },
        { texto: `Así veo a ${marca}`, estilo: 'pregunta' },
        {
          texto: p.manual
            ? 'No se pudo armar todo. Lo que falta se completa aquí y seguimos.'
            : 'No se pudo analizar todo. Lo que falta se completa aquí y seguimos.',
          estilo: 'detalle',
        },
      ];
  }
}
